import { getTableQueryFilters } from "@/Contracts/Utils";
import {
	TemplateListRequest,
	AssignUserToSignatureBlock,
	TemplateOwnerType
} from "@/Contracts/Templates";
import axios, { AxiosResponse } from "axios";

import {
	getRequest,
	postRequest,
	deleteRequest,
	putRequest
} from "@/Utils/Service/BackendService";

import {
	TemplatesResponse,
	Template,
	AddTemplateRequest,
	UpdateTemplateRequest
} from "@/Contracts/Templates";
import {
	includeChildEntities,
	ChildEntityHydrationParams
} from "@/Contracts/Utils";
import { ListPagePostRequest } from "@/Contracts/UI";
import { concatBlocksAndDraftBlockInTemplateResponse } from "@/Utils/Template";

const getCompanyTemplatesUrl = "v2/templates/company";
const getGlobalTemplatesUrl = "v2/templates/vendor";
const getAllTemplatesUrl = "v2/templates/all";
const getTemplateDetailsUrl = "/v2/templates/{template_id}";
const editTemplateUrl = "/v2/templates/{template_id}";
const addTemplatesUrl = "/v2/templates/clone";
const deleteTemplateUrl = "/v2/templates/{template_id}";
const reorderTemplateBlocksUrl = "/v2/templates/reorder/{template_id}";

const updateSignatureAssigneeUrl = "/v2/templates/assign/{template_id}";
const getTemplateListPostUrl = "/v2/templates/view";

export const getTemplateListPost = async (
	templateRequest: ListPagePostRequest
): Promise<TemplatesResponse> => {
	try {
		const response: AxiosResponse<TemplatesResponse> = await axios(
			postRequest(getTemplateListPostUrl, templateRequest, true, false, false)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getTemplates = async (
	templateListRequest: TemplateListRequest,
	templateOwnerType?: TemplateOwnerType
): Promise<TemplatesResponse> => {
	try {
		const appliedFilters =
			!!templateListRequest?.filters?.length &&
			getTableQueryFilters(templateListRequest?.filters, false);

		const filterParams = [appliedFilters].filter(a => !!a);
		const paginationParams = [
			...(templateListRequest?.limit
				? [`limit=${templateListRequest.limit}`]
				: []),
			...(templateListRequest?.page
				? [`page=${templateListRequest.page}`]
				: [])
		];
		const params = [...filterParams, ...paginationParams].join("&");

		const templatesUrl = !templateOwnerType
			? getAllTemplatesUrl
			: templateOwnerType === TemplateOwnerType.Company
			? getCompanyTemplatesUrl
			: getGlobalTemplatesUrl;

		const getUrlWithParams = [templatesUrl, params].join("?");
		const response: AxiosResponse<TemplatesResponse> = await axios(
			getRequest(getUrlWithParams, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getTemplateDetails = async (
	templateId: string,
	includeVendorEntities = false
): Promise<Template> => {
	try {
		const childEntities = [
			ChildEntityHydrationParams.BLOCKS_CURRENT_REVISION
		];
		if (includeVendorEntities) {
			childEntities.push(ChildEntityHydrationParams.VENDORS_ALL);
		}
		const includedChildEntities = includeChildEntities(childEntities);
		const url =
			getTemplateDetailsUrl.replace("{template_id}", templateId) +
			(includedChildEntities && includedChildEntities.length > 0
				? "?" + includedChildEntities
				: "");
		const response: AxiosResponse<Template> = await axios(
			getRequest(url, null, true, false, false)
		);
		return concatBlocksAndDraftBlockInTemplateResponse(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editTemplate = async (
	template: UpdateTemplateRequest
): Promise<Template> => {
	// remove blocks on edit template
	delete template.blocks;
	try {
		const response: AxiosResponse<Template> = await axios(
			putRequest(
				editTemplateUrl.replace("{template_id}", template.id),
				template,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockInTemplateResponse(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addTemplate = async (
	templateRequest: AddTemplateRequest
): Promise<Template> => {
	try {
		const response: AxiosResponse<Template> = await axios(
			postRequest(addTemplatesUrl, templateRequest, true, false, false)
		);
		return concatBlocksAndDraftBlockInTemplateResponse(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteTemplate = async (templateId: string): Promise<Template> => {
	try {
		const response: AxiosResponse<Template> = await axios(
			deleteRequest(
				deleteTemplateUrl.replace("{template_id}", templateId),
				null,
				true,
				false
			)
		);
		return concatBlocksAndDraftBlockInTemplateResponse(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateTemplateBlockList = async ({
	templateId,
	blockList
}: {
	templateId: string;
	blockList: string[];
}): Promise<Template> => {
	try {
		const includedChildEntities = [
			ChildEntityHydrationParams.BLOCKS_CURRENT_REVISION
		];
		const response: AxiosResponse<Template> = await axios(
			putRequest(
				reorderTemplateBlocksUrl.replace("{template_id}", templateId) +
					"?" +
					includeChildEntities(includedChildEntities),
				{ block_list: blockList },
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockInTemplateResponse(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateSignatureAssignee = async (
	updateSignatureAssigneeRequest: AssignUserToSignatureBlock
): Promise<Template> => {
	try {
		const url = updateSignatureAssigneeUrl.replace(
			"{template_id}",
			updateSignatureAssigneeRequest.id
		);
		const response: AxiosResponse<Template> = await axios(
			putRequest(url, updateSignatureAssigneeRequest, true, false, false)
		);
		return concatBlocksAndDraftBlockInTemplateResponse(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};
